/**
 * Admin Dashboard Zustand Store
 * Manages UI state for admin onboarding dashboard
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type ApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'all';
export type SortField = 'created_at' | 'store_name' | 'status';

export interface AdminState {
  // Filter state
  searchQuery: string;
  statusFilter: ApplicationStatus;
  sortBy: SortField;
  sortOrder: 'asc' | 'desc';
  currentPage: number;
  itemsPerPage: number;

  // Selected application
  selectedApplicationId: number | null;

  // Modal states
  detailModalOpen: boolean;
  approveDialogOpen: boolean;
  rejectDialogOpen: boolean;
  documentPreviewOpen: boolean;

  // Document preview
  selectedDocumentPath: string | null;

  // Approval/Rejection state
  approvalNotes: string;
  rejectionReason: string;
  rejectionNotes: string;

  // UI state
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;

  // Filter actions
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: ApplicationStatus) => void;
  setSortBy: (field: SortField) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;
  resetFilters: () => void;

  // Selection actions
  setSelectedApplication: (id: number | null) => void;

  // Modal actions
  openDetailModal: (applicationId: number) => void;
  closeDetailModal: () => void;
  openApproveDialog: () => void;
  closeApproveDialog: () => void;
  openRejectDialog: () => void;
  closeRejectDialog: () => void;
  openDocumentPreview: (documentPath: string) => void;
  closeDocumentPreview: () => void;

  // Approval/Rejection actions
  setApprovalNotes: (notes: string) => void;
  clearApprovalState: () => void;
  setRejectionReason: (reason: string) => void;
  setRejectionNotes: (notes: string) => void;
  clearRejectionState: () => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
}

export const useAdminStore = create<AdminState>()(
  devtools(
    (set) => ({
      // Initial state
      searchQuery: '',
      statusFilter: 'all',
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      itemsPerPage: 20,

      selectedApplicationId: null,

      detailModalOpen: false,
      approveDialogOpen: false,
      rejectDialogOpen: false,
      documentPreviewOpen: false,

      selectedDocumentPath: null,

      approvalNotes: '',
      rejectionReason: '',
      rejectionNotes: '',

      isLoading: false,
      error: null,
      successMessage: null,

      // Filter actions
      setSearchQuery: (query: string) =>
        set({ searchQuery: query, currentPage: 1 }),

      setStatusFilter: (status: ApplicationStatus) =>
        set({ statusFilter: status, currentPage: 1 }),

      setSortBy: (field: SortField) => set({ sortBy: field }),

      setSortOrder: (order: 'asc' | 'desc') => set({ sortOrder: order }),

      setCurrentPage: (page: number) => set({ currentPage: page }),

      setItemsPerPage: (items: number) =>
        set({ itemsPerPage: items, currentPage: 1 }),

      resetFilters: () =>
        set({
          searchQuery: '',
          statusFilter: 'all',
          sortBy: 'created_at',
          sortOrder: 'desc',
          currentPage: 1,
        }),

      // Selection actions
      setSelectedApplication: (id: number | null) =>
        set({ selectedApplicationId: id }),

      // Modal actions
      openDetailModal: (applicationId: number) =>
        set({
          selectedApplicationId: applicationId,
          detailModalOpen: true,
          error: null,
        }),

      closeDetailModal: () =>
        set({
          detailModalOpen: false,
          approveDialogOpen: false,
          rejectDialogOpen: false,
          selectedApplicationId: null,
          approvalNotes: '',
          rejectionReason: '',
          rejectionNotes: '',
        }),

      openApproveDialog: () => set({ approveDialogOpen: true }),

      closeApproveDialog: () =>
        set({ approveDialogOpen: false, approvalNotes: '' }),

      openRejectDialog: () => set({ rejectDialogOpen: true }),

      closeRejectDialog: () =>
        set({
          rejectDialogOpen: false,
          rejectionReason: '',
          rejectionNotes: '',
        }),

      openDocumentPreview: (documentPath: string) =>
        set({ selectedDocumentPath: documentPath, documentPreviewOpen: true }),

      closeDocumentPreview: () =>
        set({ documentPreviewOpen: false, selectedDocumentPath: null }),

      // Approval/Rejection actions
      setApprovalNotes: (notes: string) => set({ approvalNotes: notes }),

      clearApprovalState: () =>
        set({ approvalNotes: '', approveDialogOpen: false }),

      setRejectionReason: (reason: string) => set({ rejectionReason: reason }),

      setRejectionNotes: (notes: string) => set({ rejectionNotes: notes }),

      clearRejectionState: () =>
        set({
          rejectionReason: '',
          rejectionNotes: '',
          rejectDialogOpen: false,
        }),

      // UI actions
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      setSuccessMessage: (message: string | null) => set({ successMessage: message }),
    }),
    { name: 'admin-store' }
  )
);
